import React, { useEffect, useMemo, useState } from 'react';
import { Grid, makeStyles, MenuItem } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { Redirect } from 'react-router-dom';
import Select from '../components/common/Select';
import Input from '../components/common/Input';
import ContactsInfo from '../components/ContactsInfo';
import { useStores } from '../stores';
import axios from 'axios';
import WithdrawButton from '../components/Withdraw/WithdrawButton';
import { useTranslation } from 'react-i18next';
import Text from '../components/common/Text';
import appStyles from '../../../config/appStyles';
import {
  privacyPolicyLink,
  zendeskCustomFields,
  zendeskCustomFieldsTest,
  zendeskDeaChannel,
  zendeskDeaChannelTest,
  zendeskRecipient,
  zendeskWithdrawalTag,
} from '../constants';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { countryCodes } from '../../../stores/Constants';
import { detectAlpha2Country } from '../../../utils/country';

const useStyles = makeStyles((theme) => appStyles.withdraw(theme));

const Withdraw = () => {
  const classes = useStyles();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation(['components', 'validations']);
  const { uiStore, helpStore } = useStores();
  const reasons = [
    'delivery',
    'pricing',
    'affordability',
    'disinterest',
    'dealDriven',
    'quality',
    'experience',
    'support',
    'kiosk',
  ];
  const shuffledReasons = useMemo(
    () => [...reasons].sort(() => Math.random() - 0.5),
    [],
  );
  const initialFormData = {
    email: '',
    firstName: '',
    lastName: '',
    details: '',
    reason: '',
    website: '',
  };
  const [formData, setFormData] = useState(initialFormData);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_CMS_ENDPOINT}${process.env.REACT_APP_GET_CONTACT}${uiStore.site}`,
        );
        setContactInfo(res?.data?.data?.content || []);
      } catch (err) {
        console.error('Error fetching contact info:', err);
      }
    };

    fetchContactInfo();
  }, [uiStore.site]);

  function getPrivacyUrl(locale) {
    return privacyPolicyLink[locale] || privacyPolicyLink.en;
  }

  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isFormValid = () => {
    return (
      formData.email &&
      isEmailValid(formData.email) &&
      formData.firstName &&
      formData.lastName &&
      formData.details
    );
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const handleSubmit = async () => {
    if (!executeRecaptcha) return;
    if (loading) return;

    setLoading(true);

    const token = await executeRecaptcha('withdraw_form');
    const reasonText = formData.reason
      ? t(`Withdraw.reasons.${formData.reason}`)
      : null;
    const detailsText = formData.details;
    const plainText = reasonText
      ? `${reasonText}\n${detailsText}`
      : detailsText;
    const htmlBody = reasonText
      ? `<b>Reason:</b> ${reasonText}<br/><br/><b>Note:</b> ${detailsText}`
      : `<b>Note:</b> ${detailsText}`;
    const fallbackKey = countryCodes.uk?.toUpperCase();
    const zendeskRecipientValue =
      zendeskRecipient[uiStore.country] || zendeskRecipient[fallbackKey];
    const withdrawTicket = {
      ticket: {
        subject: `[Withdrawal][${process.env.REACT_APP_BRAND}${uiStore.site.toUpperCase()}]`,
        comment: {
          body: plainText,
          html_body: htmlBody,
        },
        recipient: zendeskRecipientValue[process.env.REACT_APP_BRAND],
        requester: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
        },
        tags: [zendeskWithdrawalTag],
        custom_fields: [
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.brand.id
                : zendeskCustomFieldsTest.brand.id,
            value:
              process.env.REACT_APP_BRAND === 'PW' ? 'partwork' : 'fanhome',
          },
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.country.id
                : zendeskCustomFieldsTest.country.id,
            value: uiStore.site.toUpperCase(),
          },
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.deaChannel.id
                : zendeskCustomFieldsTest.deaChannel.id,
            value:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskDeaChannel
                : zendeskDeaChannelTest,
          },
        ],
        website: formData.website,
      },
    };

    try {
      await helpStore.createWithdrawTicket(withdrawTicket, token);
      if (window.snakeAnalytics) {
        window.snakeAnalytics.push([
          'event',
          'send_request',
          {
            eventData: {
              method: formData.reason
                ? t(`Withdraw.reasons.${formData.reason}`)
                : '-',
              im_form_id: 'form_cancelacion',
              im_status: 'success',
            },
          },
        ]);
      }
      uiStore.openModal(t('Withdraw.confirmationMessage'));
      resetForm();
    } catch (error) {
      if (window.snakeAnalytics) {
        window.snakeAnalytics.push([
          'event',
          'send_request_error',
          {
            eventData: {
              im_error_message: 'API error',
              im_form_id: 'form_cancelacion',
              im_status: 'failure',
            },
          },
        ]);
      }
      uiStore.openModal(t('Withdraw.errorMessage'), '', {
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={classes.root}>
      <div className={classes.container}>
        <h1 className={classes.title}>{t('Withdraw.title')}</h1>
        <p className={classes.description}>{t('Withdraw.description')}</p>
        <ValidatorForm id="withdraw" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Input
                style={{ width: '100%' }}
                id="email"
                type="email"
                name="email"
                label={
                  <span>
                    {t('Withdraw.emailPlaceholder').replace(/\*/, '')}{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                placeholder={t('Withdraw.emailPlaceholder')}
                value={formData.email}
                onChange={handleChange}
                validators={['required', 'isEmail']}
                errorMessages={[
                  t('validations:requiredField'),
                  t('validations:emailNotValid'),
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                style={{ width: '100%' }}
                type="text"
                id="firstName"
                name="firstName"
                label={
                  <span>
                    {t('Withdraw.namePlaceholder').replace(/\*/, '')}{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                placeholder={t('Withdraw.namePlaceholder')}
                value={formData.firstName}
                onChange={handleChange}
                validators={['required']}
                errorMessages={[t('validations:requiredField')]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                style={{ width: '100%' }}
                type="text"
                id="lastName"
                name="lastName"
                label={
                  <span>
                    {t('Withdraw.surnamePlaceholder').replace(/\*/, '')}{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                placeholder={t('Withdraw.surnamePlaceholder')}
                value={formData.lastName}
                onChange={handleChange}
                validators={['required']}
                errorMessages={[t('validations:requiredField')]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                style={{ width: '100%' }}
                name="reason"
                label={t('Withdraw.reasonPlaceholder')}
                placeholder={t('Withdraw.reasonPlaceholder')}
                value={formData.reason}
                onChange={handleChange}>
                {shuffledReasons.map((el, index) => (
                  <MenuItem key={index} value={el}>
                    {t(`Withdraw.reasons.${el}`)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <Input
                style={{ width: '100%' }}
                id="details"
                name="details"
                label={
                  <span>
                    {t('Withdraw.notePlaceholder').replace(/\*/, '')}{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                placeholder={t('Withdraw.notePlaceholder')}
                rows={6}
                maxRows={6}
                multiline
                value={formData.details}
                onChange={handleChange}
                validators={['required']}
                errorMessages={[t('validations:requiredField')]}
              />
            </Grid>
            <Grid item xs={12}>
              <p className={classes.note}>{t('Withdraw.finalNote_1')}</p>
              <p
                className={classes.note}
                dangerouslySetInnerHTML={{
                  __html: t('Withdraw.finalNote_2', {
                    privacyUrl: getPrivacyUrl(detectAlpha2Country()),
                  }),
                }}
              />
            </Grid>
            <Input
              style={{ display: 'none' }}
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
            />
            <Grid item xs={12}>
              <WithdrawButton
                label={t('Withdraw.CTA_confirm')}
                isSubmit
                disabled={!isFormValid()}
                loading={loading}
              />
            </Grid>
          </Grid>
        </ValidatorForm>
      </div>
      <ContactsInfo contactInfo={contactInfo} />
    </section>
  );
};

export default Withdraw;
